import SettingsIcon from "@mui/icons-material/Settings";
import ImageIcon from "@mui/icons-material/Image";
import GridOnIcon from "@mui/icons-material/GridOn";
import StraightenIcon from "@mui/icons-material/Straighten";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import TuneIcon from "@mui/icons-material/Tune";
import MapIcon from "@mui/icons-material/Map";
import RouteIcon from "@mui/icons-material/Route";
import LabelIcon from "@mui/icons-material/Label";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import CompressIcon from "@mui/icons-material/Compress";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocalRampState, useRampEditorState } from "../../RampEditor/RampEditorPresenter";
import { useSetupWizardStore } from "./SetupWizard/useSetupWizardStore";
import { KeyboardShortcutsDialog } from "./KeyboardShortcutsDialog";
import { GISImportDialog } from "../../RampEditor/GISImport/GISImportDialog";
import { EgressCalculationDialog } from "../RampCanvasV2/EgressCalculationDialog";
import {
  calculateBlockedSpots,
  calculateSpotNames,
  EgressCalculationResult,
} from "../RampCanvasV2/spotHelpers";
import { Shape } from "../RampCanvasV2/RampCanvasV2.types";
import { useSnackbar } from "../../../../../containers/SnackbarContainer";
import { useImageUpload } from "../../RampEditor/hooks/useImageUpload";
import { compressImageServerSide } from "../../../../../services/ImageCompressionService";
import type { CompressionResult } from "../../../../../types/compression";
import type { Ramp } from "../../../../../types";

export const SettingsPanel: React.FC = () => {
  const { t } = useTranslation();
  const {
    showGuideLines,
    setShowGuideLines,
    showMeasurements,
    setShowMeasurements,
    showReferenceImage,
    setShowReferenceImage,
  } = useRampEditorState();
  const { ramp: localRamp, setRamp: setLocalRamp } = useLocalRampState();
  const { startWizard } = useSetupWizardStore();
  const snackbar = useSnackbar();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);
  const [gisImportDialogOpen, setGisImportDialogOpen] = React.useState(false);
  const [egressDialogOpen, setEgressDialogOpen] = React.useState(false);
  const [egressResult, setEgressResult] = React.useState<EgressCalculationResult | null>(null);
  const [isCompressing, setIsCompressing] = React.useState(false);

  // Image upload hook
  const { handleUpload: handleImageUpload, isUploading: isUploadingImage } = useImageUpload(
    localRamp!,
    setLocalRamp as (ramp: Ramp) => void
  );

  const hasCalibrationPoints = (localRamp?.calibration_points?.length ?? 0) >= 2;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRecalibrate = () => {
    handleClose();
    startWizard();
  };

  const handleOpenShortcuts = () => {
    handleClose();
    setShortcutsOpen(true);
  };

  const handleImportGISShapes = (newShapes: Shape[]) => {
    setLocalRamp((prev) => (prev ? { ...prev, shapes: [...prev.shapes, ...newShapes] } : prev));
    setGisImportDialogOpen(false);
    handleClose();
    snackbar.notify({
      text: `Imported ${newShapes.length} shapes`,
      severity: "success",
    });
  };

  const handleCalculateEgress = () => {
    if (!localRamp?.shapes) return;
    const result = calculateBlockedSpots(localRamp.shapes);
    setEgressResult(result);
    setLocalRamp((prev) => (prev ? { ...prev, shapes: result.shapes } : prev));
    setEgressDialogOpen(true);
    handleClose();
  };

  const handleAutoNameSpots = () => {
    if (!localRamp?.shapes) return;
    const updatedShapes = calculateSpotNames(localRamp.shapes);
    setLocalRamp((prev) => (prev ? { ...prev, shapes: updatedShapes } : prev));
    handleClose();
    const spotsCount = updatedShapes.filter((s) => s.type === "Spot").length;
    snackbar.notify({
      text: `Named ${spotsCount} spots`,
      severity: "success",
    });
  };

  const handleDownloadImage = () => {
    if (!localRamp?.reference_image) return;
    const a = document.createElement("a");
    a.href = localRamp.reference_image;
    a.download = `image-${localRamp.name}.png`;
    a.click();
  };

  const handleCompressImage = async () => {
    if (!localRamp?.reference_image || isUploadingImage || isCompressing) return;

    setIsCompressing(true);
    try {
      const response = await fetch(localRamp.reference_image);
      const blob = await response.blob();
      const file = new File([blob], "image.png", { type: blob.type });

      const result: CompressionResult = await compressImageServerSide(file);

      setLocalRamp({
        ...localRamp,
        reference_image: result.data,
      });

      const savedKB = ((result.originalSize - result.compressedSize) / 1024).toFixed(0);
      snackbar.notify({
        text: `Image compressed: saved ${savedKB}KB (${result.savings}%)`,
        severity: "success",
        duration: 4000,
      });
    } catch (error) {
      console.error("Failed to compress image:", error);
      snackbar.notify({
        text: "Failed to compress image",
        severity: "error",
      });
    } finally {
      setIsCompressing(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 0.5,
          borderRadius: 2,
        }}
      >
        <Tooltip title="View Settings" placement="right">
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{
              borderRadius: 1,
              width: 36,
              height: 36,
              backgroundColor: open ? "action.selected" : "transparent",
            }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ ml: 1 }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            View Options
          </Typography>
          <Divider sx={{ mb: 1 }} />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showReferenceImage}
                onChange={(e) => setShowReferenceImage(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ImageIcon fontSize="small" />
                <Typography variant="body2">Background Image</Typography>
              </Box>
            }
            sx={{ display: "flex", mb: 0.5 }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showMeasurements}
                onChange={(e) => setShowMeasurements(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StraightenIcon fontSize="small" />
                <Typography variant="body2">Measurements</Typography>
              </Box>
            }
            sx={{ display: "flex", mb: 0.5 }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showGuideLines}
                onChange={(e) => setShowGuideLines(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GridOnIcon fontSize="small" />
                <Typography variant="body2">Guide Lines</Typography>
              </Box>
            }
            sx={{ display: "flex" }}
          />

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Image Management
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Tooltip title="Download Image">
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownloadImage}
                  disabled={!localRamp?.reference_image}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Upload New Image">
              <span>
                <IconButton
                  size="small"
                  component="label"
                  disabled={isUploadingImage}
                >
                  {isUploadingImage ? <CircularProgress size={20} /> : <UploadIcon fontSize="small" />}
                  <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Compress Image">
              <span>
                <IconButton
                  size="small"
                  onClick={handleCompressImage}
                  disabled={isCompressing || isUploadingImage || !localRamp?.reference_image}
                >
                  {isCompressing ? <CircularProgress size={20} /> : <CompressIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {t("settings.rampsSettings.rampEditor.rampVersion", "Ramp Version")}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={localRamp?.version === "v2"}
                onChange={(e) => {
                  if (localRamp) {
                    setLocalRamp({
                      ...localRamp,
                      version: e.target.checked ? "v2" : "v1",
                    });
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TuneIcon fontSize="small" />
                <Typography variant="body2">
                  {t("settings.rampsSettings.rampEditor.v2Algorithm", "V2 Algorithm")}
                </Typography>
              </Box>
            }
            sx={{ display: "flex" }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={localRamp?.autoparking_enabled ?? false}
                onChange={() => {
                  if (localRamp) {
                    setLocalRamp({
                      ...localRamp,
                      autoparking_enabled: !localRamp.autoparking_enabled,
                    });
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalParkingIcon fontSize="small" />
                <Typography variant="body2">AutoParking</Typography>
              </Box>
            }
            sx={{ display: "flex" }}
          />

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Tools
          </Typography>

          <Button
            size="small"
            startIcon={<GpsFixedIcon />}
            onClick={handleRecalibrate}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            {t("settings.rampsSettings.rampEditor.recalibrate", "Recalibrate GPS")}
          </Button>

          <Button
            size="small"
            startIcon={<KeyboardIcon />}
            onClick={handleOpenShortcuts}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Keyboard Shortcuts
          </Button>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Bulk Operations
          </Typography>

          <Button
            size="small"
            startIcon={<MapIcon />}
            onClick={() => {
              setGisImportDialogOpen(true);
              handleClose();
            }}
            disabled={!hasCalibrationPoints}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Import GIS Shapes
          </Button>

          <Button
            size="small"
            startIcon={<RouteIcon />}
            onClick={handleCalculateEgress}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Calculate Egress
          </Button>

          <Button
            size="small"
            startIcon={<LabelIcon />}
            onClick={handleAutoNameSpots}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Auto-Name Spots
          </Button>
        </Box>
      </Popover>

      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />

      {localRamp && (
        <GISImportDialog
          open={gisImportDialogOpen}
          onClose={() => setGisImportDialogOpen(false)}
          onImportShapes={handleImportGISShapes}
          ramp={localRamp}
        />
      )}

      <EgressCalculationDialog
        open={egressDialogOpen}
        onClose={() => setEgressDialogOpen(false)}
        result={egressResult}
      />
    </>
  );
};
